import React, { useEffect, useState, useRef } from 'react';
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const ResponsiveSelect = ({ value, onChange, options, placeholder, name, required, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target))) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const selectedOpt = options.find(o => String(o.value) === String(value));
  const displayLabel = selectedOpt ? selectedOpt.label : placeholder;

  const dropdownMenu = isOpen && !disabled ? (
    <div
      ref={dropdownRef}
      className="mcs-dropdown absolute left-0 right-0 z-[9999] bg-white border border-gray-200 rounded-xl shadow-lg mt-1 overflow-y-auto max-h-[250px]"
      style={{ top: '100%' }}
    >
      {placeholder && (
        <div
          className="mcs-option px-4 py-3 border-b border-gray-50 text-left text-[15px] cursor-pointer"
          onClick={() => {
            onChange({ target: { name, value: '' } });
            setIsOpen(false);
          }}
          style={{
            backgroundColor: !value ? '#eff6ff' : 'transparent',
            color: !value ? '#1d4ed8' : '#334155',
            fontWeight: !value ? '600' : '400',
          }}
        >
          {placeholder}
        </div>
      )}
      {options.map(opt => (
        <div
          key={opt.value}
          className="mcs-option px-4 py-3 border-b border-gray-50 text-left text-[15px] cursor-pointer break-words"
          onClick={() => {
            onChange({ target: { name, value: opt.value } });
            setIsOpen(false);
          }}
          style={{
            backgroundColor: String(value) === String(opt.value) ? '#eff6ff' : 'transparent',
            color: String(value) === String(opt.value) ? '#1d4ed8' : '#334155',
            fontWeight: String(value) === String(opt.value) ? '600' : '400',
          }}
        >
          {opt.label}
        </div>
      ))}
    </div>
  ) : null;

  return (
    <div className="w-full relative" ref={wrapperRef}>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        className="hidden md:block w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white"
        style={{
          backgroundColor: disabled ? '#f8f9fa' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <div className="md:hidden block">
        <div
          className="w-full bg-white border border-gray-300 rounded-md px-4 py-2 text-sm shadow-sm transition-all duration-200 flex justify-between items-center"
          onClick={openDropdown}
          style={{
            backgroundColor: disabled ? '#f8f9fa' : 'white',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.7 : 1
          }}
        >
          <div className="text-left overflow-hidden text-ellipsis whitespace-nowrap flex-1">{displayLabel}</div>
          <span className="text-[10px] ml-2 text-gray-500">▼</span>
        </div>
        {dropdownMenu}
      </div>
    </div>
  );
};

const Designations = () => {
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDesignationId, setEditingDesignationId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState('');
  const [showAlert, setShowAlert] = useState(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const topRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    department: ""
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchDepartments(selectedCompanyId);
      fetchDesignations();
    }
  }, [selectedCompanyId]);

  const fetchCompanies = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("No access token found. Please log in.");
      setLoading(false);
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch (err) {
      console.error("Invalid token:", err);
      setError("Invalid token. Please log in again.");
      setLoading(false);
      return;
    }

    const companyId = decoded.companyId || decoded.id || decoded.company;
    if (!companyId) {
      setError("No companyId found in token.");
      setLoading(false);
      return;
    }

    // For employees/managers, skip the company API (returns 401) and use employee profile
    if (decoded.role !== 'superadmin' && decoded.role !== 'company') {
      const employeeId = decoded.id || decoded._id;
      try {
        const profileResponse = await axios.get(
          `https://crm-tkxn.onrender.com/api/employees/${employeeId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const companyData = profileResponse.data?.company;
        const businessName = (companyData && typeof companyData === 'object')
          ? companyData.businessName
          : 'My Company';
        setCompanies([{ _id: companyId, businessName }]);
      } catch (err) {
        setCompanies([{ _id: companyId, businessName: 'My Company' }]);
      }
      setSelectedCompanyId(companyId);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://crm-tkxn.onrender.com/api/companies/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCompanies([response.data]);
      setSelectedCompanyId(companyId);
    } catch (err) {
      console.error("Error fetching companies:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      setError("Failed to fetch companies.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (companyId) => {
    if (!companyId) return;
    const token = localStorage.getItem("authToken");

    try {
      const response = await axios.get(
        `https://crm-tkxn.onrender.com/api/departments/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDepartments(response.data);
    } catch (err) {
      console.error("Error fetching departments:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      setError("Failed to fetch departments.");
    }
  };

  const fetchDesignations = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("No access token found. Please log in.");
      setLoading(false);
      return;
    }

    let decoded;
    try {
      decoded = jwtDecode(token);
    } catch (err) {
      console.error("Invalid token:", err);
      localStorage.removeItem("authToken");
      setError("Invalid token. Please log in again.");
      window.location.href = '/login';
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `https://crm-tkxn.onrender.com/api/designations/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Designations API response:", response.data);

      let designationsData = [];
      if (Array.isArray(response.data)) {
        designationsData = response.data;
      } else if (response.data && Array.isArray(response.data.designations)) {
        designationsData = response.data.designations;
      } else if (response.data && Array.isArray(response.data.data)) {
        designationsData = response.data.data;
      }

      setDesignations(designationsData);
      setError(null);
    } catch (err) {
      console.error("Error fetching designations:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      setDesignations([]);
      setError(err.response?.data?.message || "Failed to fetch designations.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'name') {
      const cleanedValue = value.replace(/[^a-zA-Z\s]/g, '').replace(/\s+/g, ' ');
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: cleanedValue
      }));
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: value
      }));
    }
  };

  const handleAddDesignation = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    if (!formData.name.trim()) {
      setShowAlert("Please enter a designation name.");
      return;
    }

    if (!formData.department) {
      setShowAlert("Please select a department.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `https://crm-tkxn.onrender.com/api/designations/`,
        {
          name: formData.name.trim(),
          department: formData.department
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        }
      );

      setShowSuccessAlert("Designation created successfully!");
      fetchDesignations();
      resetForm();
    } catch (err) {
      console.error("Error creating designation:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      const errorMessage = err.response?.data?.message || "Error creating designation";
      setShowAlert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditDesignation = (designation) => {
    setIsEditing(true);
    setEditingDesignationId(designation._id);
    setFormData({
      name: designation.name || "",
      department: designation.department?._id || designation.department || ""
    });
    setShowForm(true);
    setScrollTrigger(prev => prev + 1);
  };

  useEffect(() => {
    if (showForm) {
      requestAnimationFrame(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [showForm, scrollTrigger]);

  const handleUpdateDesignation = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    if (!formData.name.trim()) {
      setShowAlert("Please enter a designation name.");
      return;
    }

    if (!formData.department) {
      setShowAlert("Please select a department.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.put(
        `https://crm-tkxn.onrender.com/api/designations/${editingDesignationId}`,
        {
          name: formData.name.trim(),
          department: formData.department
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        }
      );

      setShowSuccessAlert("Designation updated successfully!");
      fetchDesignations();
      resetForm();
    } catch (err) {
      console.error("Error updating designation:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        setError("Session expired. Please log in again.");
        window.location.href = '/login';
        return;
      }
      const errorMessage = err.response?.data?.message || "Error updating designation";
      setShowAlert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteDesignation = (designationId, designationName) => {
    setConfirmAction({
      type: 'delete',
      message: `Are you sure you want to delete the designation "${designationName}"?`,
      designationId: designationId
    });
  };

  // Execute the confirmed action
  const handleConfirmAction = async () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      setShowAlert('No access token found. Please log in.');
      setConfirmAction(null);
      return;
    }

    try {
      setSubmitting(true);

      if (confirmAction.type === 'delete') {
        const response = await axios.delete(
          `https://crm-tkxn.onrender.com/api/designations/${confirmAction.designationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            validateStatus: (status) => status < 500,
          }
        );

        if (response.status === 401) {
          setShowAlert('Session expired. Please log in again.');
          localStorage.removeItem('authToken');
          localStorage.removeItem('userInfo');
          window.location.href = '/login';
          return;
        }

        if (response.status >= 400) {
          const errorMessage = response.data?.message || `HTTP ${response.status}: Request failed`;
          setShowAlert(errorMessage);
          return;
        }

        setShowSuccessAlert('Designation deleted successfully!');
        fetchDesignations();
      }

    } catch (error) {
      console.error("Error performing designation operation:", error.response?.data || error.message || error);
      if (error.response?.status === 401) {
        setShowAlert('Session expired. Please log in again.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        window.location.href = '/login';
      } else {
        const errorMessage = error.response?.data?.message || `Error ${confirmAction.type}ing designation. Please try again.`;
        setShowAlert(errorMessage);
      }
    } finally {
      setSubmitting(false);
      setConfirmAction(null);
    }
  };

  // Close confirmation dialog
  const handleCloseAlert = () => {
    setConfirmAction(null);
  };

  // Close success alert
  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(null);
  };

  // Close error alert
  const handleCloseErrorAlert = () => {
    setShowAlert(null);
  };

  const handleDepartmentFilterChange = (e) => {
    setSelectedDepartmentFilter(e.target.value);
  };

  const filteredDesignations = selectedDepartmentFilter
    ? designations.filter(designation =>
      designation.department?._id === selectedDepartmentFilter ||
      designation.department === selectedDepartmentFilter
    )
    : designations;

  const resetForm = () => {
    setFormData({
      name: "",
      department: ""
    });
    setIsEditing(false);
    setEditingDesignationId(null);
    setShowForm(false);
  };

  if (loading && (!Array.isArray(designations) || !designations.length)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading designations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-500 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2 md:p-4">
      <div ref={topRef} className="max-w-full mx-auto"></div>
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Designation Management</h1>
              <p className="text-gray-600 mt-2">Manage designations for your company</p>
            </div>

            <button
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="mt-4 lg:mt-0 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:-translate-y-1 shadow-md"
            >
              {showForm ? "Cancel" : "Add Designation"}
            </button>
          </div>

          <div className="flex flex-col md:flex-row md:items-end gap-4">
            {/* Department Filter */}
            <div className="flex flex-col flex-1">
              <label className="text-sm font-medium text-gray-700 mb-1">Filter by Department</label>
              <ResponsiveSelect
                name="selectedDepartmentFilter"
                value={selectedDepartmentFilter}
                onChange={handleDepartmentFilterChange}
                placeholder="All Departments"
                options={departments.map(dept => ({ value: dept._id, label: dept.name }))}
              />
            </div>

            {/* Company Display */}
            <div className="flex flex-col flex-1">
              <label className="text-sm font-medium text-gray-700 mb-1">Company</label>
              <div className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm font-medium">
                {companies[0]?.businessName || 'Loading company...'}
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
            <h2 className="text-xl font-semibold mb-6 text-gray-800">
              {isEditing ? "Edit Designation" : "Add New Designation"}
            </h2>
            <form onSubmit={isEditing ? handleUpdateDesignation : handleAddDesignation}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Designation Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Manager, Developer, HR"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    required
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department *
                  </label>
                  <ResponsiveSelect
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    placeholder="Select Department"
                    required={true}
                    disabled={submitting}
                    options={departments.map(dept => ({ value: dept._id, label: dept.name }))}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50 flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {isEditing ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    isEditing ? "Update Designation" : "Add Designation"
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Designations List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-800">Designations</h2>
              <span className="text-sm text-gray-600">
                {filteredDesignations.length} designation{filteredDesignations.length !== 1 ? 's' : ''} found
              </span>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {!Array.isArray(filteredDesignations) || filteredDesignations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p className="text-lg mb-4">
                  {selectedDepartmentFilter
                    ? "No designations found for the selected department"
                    : "No designations found"
                  }
                </p>
                <button
                  onClick={() => setShowForm(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                >
                  Add First Designation
                </button>
              </div>
            ) : (
              filteredDesignations.map((designation) => (
                <div key={designation._id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{designation.name}</h3>
                      <p className="text-sm text-gray-500">
                        Department: {designation.department?.name || 'N/A'}
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleEditDesignation(designation)}
                        className="px-4 py-2 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDesignation(designation._id, designation.name)}
                        className="px-4 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Alert Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Confirmation</h2>
            <p className="text-gray-700 mb-6">{confirmAction.message}</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleConfirmAction}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Confirm'}
              </button>
              <button
                onClick={handleCloseAlert}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-xl hover:bg-gray-400 transition disabled:opacity-50"
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Alert Dialog */}
      {showSuccessAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Success</h2>
            <p className="text-gray-700 mb-6">{showSuccessAlert}</p>
            <div className="flex justify-center">
              <button
                onClick={handleCloseSuccessAlert}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert Dialog */}
      {showAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{showAlert}</p>
            <div className="flex justify-center">
              <button
                onClick={handleCloseErrorAlert}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Designations;

